import { useMemo } from "react";
import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient } from "@shell/apiClientContext";
import { ASSETS_QUERY_KEY } from "../assets/hooks";
import { ASSET_ASSIGNMENTS_QUERY_KEY } from "../asset-assignments/hooks";
import { FLOORPLAN_QUERY_KEY } from "../common/queryKeys";
import {
    addAssetToWorkspace,
    bulkImportWorkspaces,
    createWorkspace,
    deleteWorkspace,
    lookupWorkspace,
    reassignWorkspaceAssets,
    restoreWorkspace,
    searchWorkspaces,
    updateWorkspace,
} from "./api";
import type {
    AddAssetToWorkspacePayload,
    AddAssetToWorkspaceResult,
    BulkImportWorkspacesPayload,
    BulkImportWorkspacesResult,
    ReassignWorkspaceAssetsResult,
    WorkspaceUpsertPayload,
    WorkspaceView,
} from "./types";

export const WORKSPACES_QUERY_KEY = ["admin", "workspaces"] as const;
export const WORKSPACE_LOOKUP_KEY = ["admin", "workspaces", "lookup"] as const;

/**
 * Resolves a single workspace id to its view. Used by detail/label
 * components — `<WorkspaceName id={...} />` is the typical caller. Returns
 * `null` (not an error) when the id is empty or the API returns 404, so
 * callers can fall back to displaying the raw id without special-casing
 * rejection states.
 *
 * `staleTime: Infinity` is deliberate. A workspace's name and type are
 * essentially static within a session; refetching on every focus return
 * adds latency to a screen we expect to feel instantaneous after the
 * first hit. List-page mutations already invalidate `WORKSPACES_QUERY_KEY`,
 * which doesn't bust this cache key — but the detail values shown here
 * (name + type) are immutable from the user's perspective in the brief
 * window between an edit and a manual refresh, so the trade-off favors
 * speed over freshness.
 */
export function useWorkspaceEntry(
    id: string | null | undefined,
): UseQueryResult<WorkspaceView | null, Error> {
    const client = useApiClient();
    const trimmed = typeof id === "string" ? id.trim() : "";

    return useQuery({
        queryKey: [...WORKSPACE_LOOKUP_KEY, trimmed],
        queryFn: () => lookupWorkspace(client, trimmed),
        enabled: trimmed !== "",
        staleTime: Infinity,
    });
}

export interface WorkspaceDisplayNamesResult {
    readonly isLoading: boolean;
    readonly map: ReadonlyMap<string, WorkspaceView | null>;
}

/**
 * Resolves a list of workspace ids to a map of `id → view | null`.
 * Used by list views that render N rows containing workspace
 * references — `WorkspaceAssignmentsList` and `AssetAssignmentsList`
 * are the production callers — so the page computes the unique ids
 * once and passes the resulting map down to the row renderer.
 *
 * Mirrors `useUserDisplayNames`: inputs are deduplicated and sorted
 * for stable query keys; each id is fetched as an independent query
 * so the cache is shared with `useWorkspaceEntry` (a detail view
 * opened for the same workspace reuses the list's already-fetched
 * entry without a second round-trip). This pattern avoids the
 * `SearchWorkspacesEndpoint` 200-row cap that bites a single bulk
 * prefetch in tenants with hundreds of workspaces.
 *
 * The returned map always has an entry for every input id, valued
 * `null` while pending or when the API returns 404 — callers render
 * the raw id as a fallback in either case.
 */
export function useWorkspaceDisplayNames(
    ids: readonly string[],
): WorkspaceDisplayNamesResult {
    const client = useApiClient();

    const unique = useMemo(() => {
        const seen = new Set<string>();
        for (const raw of ids) {
            const trimmed = raw.trim();
            if (trimmed === "") continue;
            seen.add(trimmed);
        }
        return Array.from(seen).sort();
    }, [ids]);

    return useQueries({
        queries: unique.map((id) => ({
            queryKey: [...WORKSPACE_LOOKUP_KEY, id],
            queryFn: () => lookupWorkspace(client, id),
            staleTime: Infinity,
        })),
        combine: (results) => {
            const map = new Map<string, WorkspaceView | null>();
            for (let i = 0; i < unique.length; i += 1) {
                const id = unique[i];
                if (id === undefined) continue;
                map.set(id, results[i]?.data ?? null);
            }
            return {
                isLoading: results.some((r) => r.isPending),
                map,
            };
        },
    });
}

export interface SearchWorkspacesParams {
    readonly q: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly includeDeleted: boolean;
    readonly floorplanOnly?: boolean;
}

export function useWorkspaces(
    params: SearchWorkspacesParams,
): UseQueryResult<readonly WorkspaceView[], Error> {
    const client = useApiClient();
    return useQuery({
        queryKey: [
            ...WORKSPACES_QUERY_KEY,
            params.q,
            params.sageOfficeId,
            params.type,
            params.includeDeleted,
            params.floorplanOnly === true,
        ],
        queryFn: () => searchWorkspaces(client, params),
        staleTime: 30_000,
    });
}

export function useCreateWorkspace(): UseMutationResult<
    WorkspaceView,
    Error,
    WorkspaceUpsertPayload
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => createWorkspace(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}

export function useUpdateWorkspace(): UseMutationResult<
    WorkspaceView,
    Error,
    WorkspaceUpsertPayload
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => updateWorkspace(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}

export function useDeleteWorkspace(): UseMutationResult<void, Error, string> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => deleteWorkspace(client, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}

export function useRestoreWorkspace(): UseMutationResult<WorkspaceView, Error, string> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => restoreWorkspace(client, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}

export interface AddAssetToWorkspaceInput {
    readonly workspaceId: string;
    readonly payload: AddAssetToWorkspacePayload;
}

/**
 * Mutation for the workspace-anchored "add asset" call. One round-trip
 * creates both the asset and the workspace assignment atomically; the
 * `onSuccess` invalidates every cache that could be showing the new rows
 * — assets list, asset-assignments list, and workspaces list (since the
 * workspace's own `currentAssignmentId` may have moved).
 */
export function useAddAssetToWorkspace(): UseMutationResult<
    AddAssetToWorkspaceResult,
    Error,
    AddAssetToWorkspaceInput
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ workspaceId, payload }) => addAssetToWorkspace(client, workspaceId, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: ASSET_ASSIGNMENTS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
        },
    });
}

export interface ReassignWorkspaceAssetsInput {
    readonly sourceWorkspaceId: string;
    readonly targetWorkspaceId: string;
}

/**
 * "Reassign All" mutation. Like bulk import, a partial failure still
 * resolves `onSuccess` (the HTTP call succeeded); the caller inspects
 * `data.failed`. Invalidation runs unconditionally since some assets may
 * have moved even when others failed.
 */
export function useReassignWorkspaceAssets(): UseMutationResult<
    ReassignWorkspaceAssetsResult,
    Error,
    ReassignWorkspaceAssetsInput
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sourceWorkspaceId, targetWorkspaceId }) =>
            reassignWorkspaceAssets(client, sourceWorkspaceId, targetWorkspaceId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: ASSET_ASSIGNMENTS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}

/**
 * Bulk-import mutation. Even partial-success responses (some rows
 * failed) trip `onSuccess` because the HTTP call itself succeeded —
 * the page is responsible for reading `data.failed` and surfacing
 * per-row outcomes. The cache invalidation runs unconditionally so
 * the workspaces list reflects newly-created rows immediately.
 */
export function useBulkImportWorkspaces(): UseMutationResult<
    BulkImportWorkspacesResult,
    Error,
    BulkImportWorkspacesPayload
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => bulkImportWorkspaces(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}
